import { useMemo } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDensity } from "@/contexts/DensityContext";
import { BankTransactionDto, BankTransactionPagination } from "@blinkbooks/types";
import { format, parseISO } from "date-fns";

interface StatementViewProps {
  transactions: BankTransactionDto[];
  openingBalance: number;
  loading?: boolean;
  pagination: BankTransactionPagination;
  currentPage: number;
  onPageChange: (page: number) => void;
  currency?: string;
}

const formatCurrency = (amount: number, currency = "USD") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateStr: string) => {
  try {
    return format(parseISO(dateStr), "MMM d, yyyy");
  } catch {
    return dateStr;
  }
};

const StatementView = ({
  transactions,
  openingBalance,
  loading = false,
  pagination,
  currentPage,
  onPageChange,
  currency = "USD",
}: StatementViewProps) => {
  const { density } = useDensity();
  const rowPadY = density === "compact" ? "py-1" : "py-2";
  const rowHCls = density === "compact" ? "h-8" : "h-11";

  // Transactions arrive DESC (newest first). Compute balances in ASC order, then re-reverse.
  const rowsWithBalance = useMemo(() => {
    const asc = [...transactions].reverse();
    let balance = Number(openingBalance);
    const withBal = asc.map((txn) => {
      balance += txn.direction === "IN" ? Number(txn.amount) : -Number(txn.amount);
      return { ...txn, runningBalance: balance };
    });
    return withBal.reverse(); // back to DESC for display
  }, [transactions, openingBalance]);

  // Most recent transaction is first in the DESC list
  const closingBalance = rowsWithBalance.length > 0
    ? rowsWithBalance[0]!.runningBalance
    : openingBalance;

  return (
    <div className="space-y-4">
      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-[110px]">Date</TableHead>
              <TableHead className="min-w-[140px]">Merchant</TableHead>
              <TableHead className="min-w-[180px] max-w-[260px]">Description</TableHead>
              <TableHead className="text-right w-[120px]">Debit (Out)</TableHead>
              <TableHead className="text-right w-[120px]">Credit (In)</TableHead>
              <TableHead className="text-right w-[130px] font-semibold">Balance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">Loading statement...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No transactions found
                </TableCell>
              </TableRow>
            ) : (
              <>
                {/* Closing balance row — newest first, so closing is at the top */}
                <TableRow className="bg-muted/20 font-medium text-sm border-b-2">
                  <TableCell className={cn(rowPadY, "text-muted-foreground italic")} colSpan={3}>
                    Closing Balance
                  </TableCell>
                  <TableCell className={rowPadY} />
                  <TableCell className={rowPadY} />
                  <TableCell
                    className={cn(rowPadY, "text-right font-semibold tabular-nums", closingBalance < 0 && "text-red-600")}
                  >
                    {formatCurrency(closingBalance, currency)}
                  </TableCell>
                </TableRow>

                {/* Transaction rows — newest to oldest */}
                {rowsWithBalance.map((txn) => (
                  <TableRow key={String(txn.id)} className={rowHCls}>
                    <TableCell className={cn(rowPadY, "whitespace-nowrap text-sm")}>
                      {formatDate(txn.transactionDate)}
                    </TableCell>
                    <TableCell className={cn(rowPadY, "text-sm font-medium")}>
                      {txn.merchantName}
                    </TableCell>
                    <TableCell
                      className={cn(rowPadY, "text-sm text-muted-foreground max-w-[260px] truncate")}
                      title={txn.description}
                    >
                      {txn.description}
                    </TableCell>
                    <TableCell className={cn(rowPadY, "text-right tabular-nums text-sm")}>
                      {txn.direction === "OUT" && (
                        <span className="text-red-600">
                          {formatCurrency(Number(txn.amount), currency)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={cn(rowPadY, "text-right tabular-nums text-sm")}>
                      {txn.direction === "IN" && (
                        <span className="text-green-600">
                          {formatCurrency(Number(txn.amount), currency)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell
                      className={cn(
                        rowPadY,
                        "text-right tabular-nums text-sm font-medium",
                        txn.runningBalance < 0 && "text-red-600",
                      )}
                    >
                      {formatCurrency(txn.runningBalance, currency)}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Opening balance row — oldest, so at the bottom */}
                <TableRow className="bg-muted/20 font-medium text-sm border-t-2">
                  <TableCell className={cn(rowPadY, "text-muted-foreground italic")} colSpan={3}>
                    Opening Balance
                  </TableCell>
                  <TableCell className={rowPadY} />
                  <TableCell className={rowPadY} />
                  <TableCell className={cn(rowPadY, "text-right font-semibold tabular-nums")}>
                    {formatCurrency(openingBalance, currency)}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t pt-4">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * pagination.limit) + 1}–
            {Math.min(currentPage * pagination.limit, pagination.total)} of{" "}
            {pagination.total} transactions
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1 || loading}
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum: number;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === currentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(pageNum)}
                    disabled={loading}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= pagination.totalPages || loading}
              className="gap-1"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatementView;
